'use client'

// components/MarkdownRenderer.js
import Link from 'next/link'
import Image from 'next/image'
import React, { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkRehype from 'remark-rehype'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'
import rehypeExternalLinks from 'rehype-external-links'
import rehypeSlug from 'rehype-slug'
import remarkBreaks from 'remark-breaks'

// Components
import { CopyButton } from '@/components/shared/copy-button'

// Utils
import { cn } from '@/lib/utils'

interface MarkdownRendererProps {
  content: string
}

export function MarkdownRenderer({ content }: MarkdownRendererProps) {
  const [modalImage, setModalImage] = useState<string | null>(null)

  const components = {
    pre({ node, inline, className, children, ...props }) {
      const codeText = String(children.props.children)

      return (
        <div className="group relative w-full overflow-hidden">
          <pre
            className={cn(
              '!mt-0 max-w-[calc(100vw-90px)] whitespace-pre-wrap md:max-w-[calc(100vw-350px)] lg:max-w-none',
              className
            )}
            {...props}
          >
            {children}
          </pre>
          {codeText && (
            <CopyButton
              value={codeText}
              className={cn(
                'absolute right-8 top-4 z-20 lg:right-4',
                'duration-250 opacity-0 transition-all group-hover:opacity-100'
              )}
            />
          )}
        </div>
      )
    },
    code({ node, inline, className, children, ...props }) {
      return (
        <code className={cn('', className)} {...props}>
          {children}
        </code>
      )
    },
    img: ({
      className,
      alt,
      src,
      ...props
    }: React.ImgHTMLAttributes<HTMLImageElement>) => {
      return (
        <span className="grid min-h-[140px] w-full place-items-center overflow-x-scroll rounded-lg lg:overflow-visible">
          <span
            onClick={(e) => {
              // Check if image is not already inside a link
              const target = e.target as HTMLElement
              if (
                !target.closest('a[href]') &&
                src &&
                typeof src === 'string'
              ) {
                e.preventDefault()
                setModalImage(src)
              }
            }}
            className="group relative block cursor-pointer"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              className={cn(
                'max-h-[600px] w-auto max-w-full rounded-lg object-contain transition-transform duration-300 group-hover:scale-[1.02]',
                className
              )}
              alt={alt}
              src={src}
              {...props}
            />
            <span className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-300 group-hover:bg-black/10 group-hover:opacity-100">
              <span className="rounded-lg bg-black/75 px-4 py-2 text-sm text-white">
                Click to view full size
              </span>
            </span>
          </span>
        </span>
      )
    },
    Step: ({ className, ...props }: React.ComponentProps<'h3'>) => (
      <h3
        className={cn(
          'mt-8 scroll-m-20 font-heading text-xl font-semibold tracking-tight',
          className
        )}
        {...props}
      />
    ),
    Steps: ({ ...props }) => (
      <div
        className="[&>h3]:step steps mb-12 ml-4 border-l pl-8 [counter-reset:step]"
        {...props}
      />
    ),
    Link: ({ className, ...props }: React.ComponentProps<typeof Link>) => (
      <Link
        className={cn('font-medium underline underline-offset-4', className)}
        {...props}
      />
    ),
    LinkedCard: ({
      className,
      ...props
    }: React.ComponentProps<typeof Link>) => (
      <Link
        className={cn(
          'flex w-full flex-col items-center rounded-xl border bg-card p-6 text-card-foreground shadow transition-colors hover:bg-muted/50 sm:p-10',
          className
        )}
        {...props}
      />
    ),
    Image: ({
      src,
      alt,
      className,
      ...props
    }: React.ComponentProps<typeof Image>) => (
      <span className="my-6 grid min-h-[140px] w-full place-items-center overflow-x-scroll rounded-lg p-4 lg:overflow-visible">
        <Image
          className={cn(
            'h-96 w-full rounded-lg object-cover object-center',
            className
          )}
          src={src}
          alt={alt || ''}
          width={0}
          height={0}
          {...props}
        />
      </span>
    ),
  }

  return (
    <>
      <div className="prose !max-w-none px-4 pb-4 dark:prose-invert prose-h2:scroll-mt-24 prose-h3:scroll-mt-24 prose-hr:-mx-4">
        <ReactMarkdown
          components={components}
          remarkPlugins={[remarkGfm, remarkBreaks, remarkRehype]}
          rehypePlugins={[
            rehypeRaw,
            rehypeSlug,
            [
              rehypeExternalLinks,
              { target: '_blank', rel: ['noopener noreferrer'] },
            ],
          ]}
        >
          {content}
        </ReactMarkdown>
      </div>

      {/* Image Modal */}
      {modalImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setModalImage(null)}
        >
          <button
            onClick={() => setModalImage(null)}
            className="absolute right-4 top-4 rounded-lg bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={modalImage}
            alt="Full size"
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  )
}
